import React, { useState, useEffect } from 'react';
import GlobalNavigation from '@components/GlobalNavigation';
import CardDisplay from '@components/CardDisplay';
import ProductList from '@components/ProductList';
import CategoryTabs from '@components/CategoryTabs';
import CategoryTabsWithModal from '@components/CategoryTabsWithModal';
import ExpressCart from '@components/ExpressCart';
import SocialIcons from '@components/SocialIcons';
import StoreNavigation from '@components/StoreNavigation';
import GlobalFooter from '@components/GlobalFooter';
import GlobalFooterWithSellerInfo from '@components/GlobalFooterWithSellerInfo';
import Button from '@material-ui/core/Button';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { Typography, Grid, Divider } from '@material-ui/core';
import Head from 'next/head';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import StoreInfo from '@components/StoreInfo';
import request from '@utils/request';
import _get from 'lodash/get';
import { inject, observer } from 'mobx-react';
import _isEmpty from 'lodash/isEmpty';
import { isLoggedIn } from '@utils/account';
import CustomSnackbar from '@components/CustomSnackbar';
import { localSubdomain } from '@constants/index';
import { getResponseErrors, getFinalLocale } from '@utils/store';
import AdSense from 'react-ssr-adsense';
import useWindowSize from '@hooks/useWindowSize';
import Carousel from '@components/Carousel';
import ProductItem from '@components/ProductItem';
import ProductCarousel from '@components/ProductCarousel';
import { isShowAdSense } from '../../utils/store';

const useStyles = makeStyles(theme => ({
  root: {},
  contentContainer: {
    paddingBottom: 70,
    [theme.breakpoints.up('md')]: {
      paddingBottom: 0,
    },
  },
  orderSummaryCardRoot: {
    position: 'sticky',
    top: 0,
  },
  tabWrapper: {
    textTransform: 'capitalize',
  },
  orderSummaryWrapper: {
    display: 'none',
    [theme.breakpoints.up('md')]: {
      display: 'block',
    },
  },
  totalItems: {
    whiteSpace: 'noWrap',
  },
  actionWrapper: {
    zIndex: 100,
    position: 'fixed',
    bottom: 0,
    width: '100%',
    padding: '12.5px 24.5px',
    backgroundColor: '#fff',
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
  },
  buttonRoot: {
    width: '100%',
    display: 'flex',
  },
  buttonLabel: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  adsWrapper: {
    position: 'relative',
    padding: '20px 0',
  },
  webStoreContentWrapper: {
    textAlign: 'center',
  },
  productList: {
    maxWidth: 1000,
    margin: '64px auto 32px auto',
  },
  productListTitle: {
    textAlign: 'center',
    fontWeight: 'normal',
  },
}));

const THEME_V1 = 'v1';
const THEME_V2 = 'v2';

const Index = inject('store')(
  observer(
    ({
      locale = '',
      webStore = {},
      subdomain,
      domainPath,
      storeTheme = THEME_V1,
      store: {
        orderStore,
        orderStore: { tempOrder = {}, order = {} },
        accountStore: { me = {} },
      },
      categories = [],
    }) => {
      const { t } = useTranslation('common');
      const classes = useStyles();
      const router = useRouter();
      const { width } = useWindowSize();
      const [snackBarType, setSnackBarType] = useState('error');
      const [errorMessage, setErrorMessage] = useState('');
      const [currentTab, setCurrentTab] = useState(0);
      const [currentCategorySlug, setCurrentCategorySlug] = useState(null);
      const [keyword, setKeyword] = useState('');
      const currency = _get(webStore, 'store_currency', 'MYR');
      const urlKeyword = _get(router, 'query.q');

      useEffect(() => {
        if (urlKeyword) {
          setKeyword(urlKeyword);
        }
      }, [urlKeyword]);

      useEffect(() => {
        if (categories.length > 0) {
          if (window.location.hash) {
            const hashArr = window.location.hash.split('#');
            if (hashArr[1]) {
              const _slug = decodeURIComponent(hashArr[1]);
              const res = categories.filter(({ slug }) => slug === _slug);
              if (res.length > 0) {
                setCurrentCategorySlug(_slug);
                return false;
              }
            }
          }
          setCurrentCategorySlug(_get(categories, '0.slug'));
        }
      }, [categories]);

      useEffect(() => {
        if (!_isEmpty(me)) {
          orderStore.getCart({ subdomain });
        }
      }, [me, subdomain, orderStore]);

      const handleChangeCurrentTab = (_, newValue) => {
        setCurrentTab(newValue);
      };

      const placeOrder = () => {
        const orderItems = isLoggedIn()
          ? _get(order, 'order_items', [])
          : _get(tempOrder, 'order_items', []);
        if (orderItems.length === 0) {
          setSnackBarType('error');
          setErrorMessage(t('no-items-added'));
          return false;
        }
        if (!_isEmpty(me)) {
          if (_get(order, 'id')) {
            router.push(`${domainPath}/cart?id=${_get(order, 'id')}`);
          } else {
            console.error('No order Id');
          }
        } else {
          router.push(`${domainPath}/permission?subdomain=${subdomain}`);
        }
      };

      const onChangeCartItem = x => {
        orderStore
          .putOrder({
            order_id: _get(order, 'id'),
            order_items: x,
          })
          .then(res => {
            if (_get(res, 'status', false) === false) {
              setSnackBarType('error');
              const errors = getResponseErrors(res);
              setErrorMessage(errors);
            }
          });
      };

      const getQuantityCount = () => {
        const orderItems = isLoggedIn()
          ? _get(order, 'order_items', [])
          : _get(tempOrder, 'order_items', []);
        let totalQuantity = 0;
        orderItems.map(({ quantity = 0 }) => (totalQuantity += quantity));
        return totalQuantity;
      };

      const HeadHtml = () => {
        return (
          <Head>
            <title>{_get(webStore, 'title')} - SellOnLive</title>
            <meta property="og:title" content={_get(webStore, 'title')} />
            <meta property="og:description" content={_get(webStore, 'description')} />
            <meta property="og:image" content={_get(webStore, 'facebook_page.cover_url')} />
            <meta property="og:url" content={`https://${subdomain}.sellonlive.tech/store`} />
          </Head>
        );
      };

      const AdsLeaderBoard = () =>
        isShowAdSense({
          facebookPageId: webStore?.facebook_page?.id,
        }) ? (
          <>
            {width > 768 ? (
              <div className={classes.adsWrapper}>
                <AdSense
                  client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                  slot="1756571623"
                  style={{
                    display: 'block',
                    width: 1024,
                    height: 100,
                    margin: '0 auto',
                    textAlign: 'center',
                  }}
                  format=""
                />
              </div>
            ) : (
              <div className={classes.adsWrapper}>
                <AdSense
                  client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                  slot="5171689560"
                  style={{ display: 'block', width: 320, height: 100, margin: '0 auto' }}
                  format=""
                />
              </div>
            )}
          </>
        ) : (
          <br />
        );

      if (typeof window === 'object') {
        if (
          (window.navigator.userAgent.indexOf('FBAV') > -1 ||
            window.navigator.userAgent.indexOf('FBAN') > -1) &&
          window.navigator.userAgent.indexOf('Android') > -1
        ) {
          useEffect(() => {
            if (typeof window === 'object') {
              window.location.href =
                'intent:' +
                `https://${subdomain}.sellonlive.tech/store` +
                '#Intent;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;component=com.android.browser/com.android.browser.BrowserActivity;end';
            }
          }, []);

          return (
            <div>
              <HeadHtml />
              <GlobalNavigation isShowLogo={false} subdomain={subdomain} />
              <div className="container">
                <AdsLeaderBoard />
                <CardDisplay>
                  <div style={{ textAlign: 'center' }}>
                    <Typography align="center">Please view it on your app browser</Typography>
                    <br />
                    <Button
                      color="primary"
                      variant="contained"
                      size="large"
                      href={
                        'intent:' +
                        `https://${subdomain}.sellonlive.tech/store` +
                        '#Intent;action=android.intent.action.VIEW;category=android.intent.category.BROWSABLE;component=com.android.browser/com.android.browser.BrowserActivity;end'
                      }
                    >
                      Open browser
                    </Button>
                  </div>
                </CardDisplay>
              </div>
            </div>
          );
        }
      }

      if (storeTheme === THEME_V1) {
        return (
          <div>
            <HeadHtml />
            <CustomSnackbar
              type={snackBarType}
              errorMessage={errorMessage}
              closeCallback={() => setErrorMessage('')}
            />
            <GlobalNavigation isShowLogo={false} subdomain={subdomain} />
            <div className="container">
              <AdsLeaderBoard />
              <Grid container className={classes.contentContainer} spacing={3}>
                <Grid item xs={12} md={8}>
                  <CardDisplay noPadding={true}>
                    <StoreInfo
                      storeName={_get(webStore, 'title') || _get(webStore, 'facebook_page.name')}
                      storeImage={`${_get(webStore, 'facebook_page.picture_url')}`}
                      bannerImage={_get(webStore, 'facebook_page.cover_url')}
                      storeDescription={_get(webStore, 'description')}
                    />
                    <Divider />
                    <Tabs
                      indicatorColor="primary"
                      value={currentTab}
                      onChange={handleChangeCurrentTab}
                    >
                      <Tab
                        disableRipple
                        label={t('products')}
                        icon={<img src="/static/images/icon-cart.svg" />}
                        classes={{
                          wrapper: classes.tabWrapper,
                        }}
                      />
                      <Tab
                        disableRipple
                        label={t('about-us')}
                        icon={<img src="/static/images/icon-store.svg" />}
                        classes={{
                          wrapper: classes.tabWrapper,
                        }}
                      />
                    </Tabs>
                  </CardDisplay>
                  <br />
                  {currentTab === 0 ? (
                    <>
                      {width > 768 ? (
                        <CategoryTabs
                          categories={categories}
                          setKeyword={setKeyword}
                          setCurrentCategorySlug={setCurrentCategorySlug}
                        />
                      ) : (
                        <CategoryTabsWithModal
                          categories={categories}
                          setKeyword={setKeyword}
                          currentCategorySlug={currentCategorySlug}
                          setCurrentCategorySlug={setCurrentCategorySlug}
                        />
                      )}
                    </>
                  ) : null}
                  {currentTab === 0 ? <br /> : null}
                  <CardDisplay noPadding={true}>
                    {currentTab === 0 ? (
                      <ProductList
                        locale={locale}
                        categories={categories}
                        keyword={keyword}
                        setKeyword={setKeyword}
                        currentCategorySlug={currentCategorySlug}
                      />
                    ) : null}
                    {currentTab === 1 ? (
                      <CardDisplay>
                        <Typography>
                          <b>{t('about-us')}</b>
                        </Typography>
                        <Divider />
                        <Typography
                          dangerouslySetInnerHTML={{
                            __html: _get(webStore, 'about_us') || '-',
                          }}
                        ></Typography>
                        <br />
                        <br />
                        <br />
                        <Typography>
                          <b>{t('business-details')}</b>
                        </Typography>
                        <Divider />
                        <Typography
                          dangerouslySetInnerHTML={{
                            __html: _get(webStore, 'business_details') || '-',
                          }}
                        ></Typography>
                        <br />
                        <br />
                        <br />
                        {_get(webStore, 'contacts.fb_url') ||
                        _get(webStore, 'contacts.whatsapp_url') ||
                        _get(webStore, 'contacts.ig_url') ? (
                          <>
                            <Typography>
                              <b>{t('contact-us')}</b>
                            </Typography>
                            <Divider />
                            <SocialIcons
                              facebookLink={_get(webStore, 'contacts.fb_url')}
                              whatsappLink={_get(webStore, 'contacts.whatsapp_url')}
                              igLink={_get(webStore, 'contacts.ig_url')}
                            />
                            <br />
                            <br />
                            <br />
                          </>
                        ) : null}
                      </CardDisplay>
                    ) : null}
                  </CardDisplay>
                  {isShowAdSense({
                    facebookPageId: webStore?.facebook_page?.id,
                  }) ? (
                    width > 768 ? (
                      <div className={classes.adsWrapper}>
                        <AdSense
                          client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                          slot="7606281213"
                          style={{ display: 'block' }}
                          format="auto"
                          responsive="true"
                        />
                      </div>
                    ) : (
                      <div className={classes.adsWrapper}>
                        <AdSense
                          client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                          slot="6051039857"
                          style={{ display: 'block', width: 320, height: 250, margin: '0 auto' }}
                          format=""
                        />
                      </div>
                    )
                  ) : null}
                  <br />
                </Grid>
                <Grid item className={classes.orderSummaryWrapper} xs="auto" md={4}>
                  <div className={classes.orderSummaryCardRoot}>
                    <CardDisplay title={t('order-summary')}>
                      <ExpressCart
                        isAllowToChangeQuantity={_get(webStore, 'allow_change_cart', false)}
                        isAllowToChangeWebStoreQuantity={_get(
                          webStore,
                          'allow_change_webstore_cart',
                          false
                        )}
                        isStoreAllowHoldShipping={false}
                        currency={currency}
                        data={
                          isLoggedIn()
                            ? _get(order, 'order_items', [])
                            : _get(tempOrder, 'order_items', [])
                        }
                        subTotal={
                          isLoggedIn() ? _get(order, 'subtotal', 0) : _get(tempOrder, 'subtotal', 0)
                        }
                        onChangeCartItem={e =>
                          isLoggedIn() ? onChangeCartItem(e) : orderStore.updateOrderItems(e)
                        }
                        loading={false}
                        onClickPlaceOrder={placeOrder}
                      />
                    </CardDisplay>
                    {process.env.NEXT_PUBLIC_GOOGLE_ADS_ID && width > 1024 ? (
                      <>
                        <br />
                        <AdSense
                          client={process.env.NEXT_PUBLIC_GOOGLE_ADS_ID}
                          slot="6059683999"
                          style={{ display: 'block' }}
                          format="auto"
                          responsive="true"
                        />
                      </>
                    ) : null}
                    <br />
                    <br />
                  </div>
                </Grid>
              </Grid>
            </div>
            <div className={classes.actionWrapper}>
              <Button
                color="primary"
                variant="contained"
                size="large"
                classes={{
                  root: classes.buttonRoot,
                  label: classes.buttonLabel,
                }}
                onClick={placeOrder}
              >
                <div>{t('cart')}</div>
                <div className={classes.totalItems}>
                  {getQuantityCount()} {t('item')}
                </div>
                <div>
                  {currency}
                  {isLoggedIn()
                    ? _get(order, 'subtotal', 0).toFixed(2)
                    : _get(tempOrder, 'subtotal', 0).toFixed(2)}
                </div>
              </Button>
            </div>
            {_get(webStore, 'facebook_page.id') && _get(webStore, 'facebook_page.name') ? (
              <GlobalFooterWithSellerInfo
                facebookPageId={_get(webStore, 'facebook_page.id')}
                facebookPageName={_get(webStore, 'facebook_page.name')}
              />
            ) : (
              <GlobalFooter />
            )}
          </div>
        );
      }
      if (storeTheme === THEME_V2) {
        return (
          <div>
            <HeadHtml />
            <CustomSnackbar
              type={snackBarType}
              errorMessage={errorMessage}
              closeCallback={() => setErrorMessage('')}
            />
            <GlobalNavigation isShowLogo={false} subdomain={subdomain} />
            <div className="container">
              <AdsLeaderBoard />
              <StoreNavigation
                facebookPageId={_get(webStore, 'facebook_page.id')}
                facebookPageName={_get(webStore, 'facebook_page.name')}
              />
              <Grid container className={classes.contentContainer}>
                <Grid item xs={12}>
                  <Carousel
                    images={[
                      {
                        url:
                          'https://images.unsplash.com/photo-1640505541901-7b654a6ebf9f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2370&q=20',
                      },
                      {
                        url:
                          'https://images.unsplash.com/photo-1640505541901-7b654a6ebf9f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2370&q=20',
                      },
                      {
                        url:
                          'https://images.unsplash.com/photo-1640505541901-7b654a6ebf9f?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2370&q=20',
                      },
                    ]}
                  />
                  <div className={classes.webStoreContentWrapper}>
                    <p>{_get(webStore, 'description')}</p>
                    <Button color="secondary" variant="contained">
                      全部商品
                    </Button>
                  </div>
                  <div className={classes.productList}>
                    <h1 className={classes.productListTitle}>Recommended Products 推荐商品</h1>
                    <ProductCarousel>
                      {Array.from(Array(10).keys()).map((data, i) => (
                        <ProductItem
                          key={i}
                          direction="vertical"
                          price={100}
                          productImage={
                            'https://docs.sellonlive.tech/storage/products/f5f57335-c130-47b4-ad52-814efde8e7aa-c1ab0495-9b0b-452e-8440-d14ec54385e8.jpg'
                          }
                          productName={'DS 30164 爆款棉质tee'}
                          slug={'slug'}
                          is_available={true}
                          locale={locale}
                        />
                      ))}
                    </ProductCarousel>
                  </div>
                  <p>// featured collection</p>
                </Grid>
              </Grid>
            </div>
            <div className={classes.actionWrapper}>
              <Button
                color="primary"
                variant="contained"
                size="large"
                classes={{
                  root: classes.buttonRoot,
                  label: classes.buttonLabel,
                }}
                onClick={placeOrder}
              >
                <div>{t('cart')}</div>
                <div className={classes.totalItems}>
                  {getQuantityCount()} {t('item')}
                </div>
                <div>
                  {currency}
                  {isLoggedIn()
                    ? _get(order, 'subtotal', 0).toFixed(2)
                    : _get(tempOrder, 'subtotal', 0).toFixed(2)}
                </div>
              </Button>
            </div>
            {_get(webStore, 'facebook_page.id') && _get(webStore, 'facebook_page.name') ? (
              <GlobalFooterWithSellerInfo
                facebookPageId={_get(webStore, 'facebook_page.id')}
                facebookPageName={_get(webStore, 'facebook_page.name')}
              />
            ) : (
              <GlobalFooter />
            )}
          </div>
        );
      }
    }
  )
);

export const getServerSideProps = async ({ locale, ...props }) => {
  const host = _get(props, 'req.headers.host', '');
  const localeFromCookies = _get(props, 'req.cookies.language');
  let subdomain = host ? host.split('.')[0] : '';

  if (process.env.NEXT_PUBLIC_COOKIE_DOMAIN === 'localhost') {
    subdomain = localSubdomain;
  }
  let webStore = {};
  let categories = [];
  if (subdomain) {
    webStore = await request(`${process.env.NEXT_PUBLIC_API_SERVER}/store/${subdomain}`, {
      method: 'GET',
    }).then(res => {
      return _get(res, 'data.data', {});
    });
    categories = await request(
      `${process.env.NEXT_PUBLIC_API_SERVER}/store/${subdomain}/products`,
      {
        method: 'GET',
      }
    ).then(res => {
      return _get(res, 'data.data', []);
    });
  }

  let storeTheme = THEME_V1;
  let redirect = false;
  const { finalLocale, needRedirect } = getFinalLocale({
    localeFromCookies,
    localeFromServer: locale,
    storeLocale: _get(webStore, 'auto_reply_language', 'en'),
  });

  if (needRedirect) {
    redirect = {
      destination: `/${finalLocale}/store`,
    };
  }
  return {
    notFound: _isEmpty(webStore),
    redirect,
    props: {
      subdomain,
      locale: finalLocale,
      webStore,
      categories,
      storeTheme,
      host: subdomain,
      domainPath:
        locale === 'en'
          ? process.env.NEXT_PUBLIC_DOMAIN_URL
          : `${process.env.NEXT_PUBLIC_DOMAIN_URL}/${locale}`,
      ...(await serverSideTranslations(locale, ['common'])),
    },
  };
};

export default Index;
